import { Box, Button, HStack, ModalBody, ModalFooter, Text, VStack } from '@chakra-ui/react';
import { TRIAL_CARDLESS_SEAT_CAP } from '@bower/shared/billing/policy';
import { StatusBanner } from '../ui/StatusBanner';

export function formatUsd(cents: number, currency: string): string {
  if (currency.toLowerCase() === 'usd') {
    return `$${(cents / 100).toFixed(2)}`;
  }
  return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

export type SeatPaymentMethodReason = 'cap-reached' | null;

/** Narrow the backend's reason for demanding a payment method. Anything
 *  unrecognised, including the field an older backend does not send and the
 *  retired `allowance-closed`, becomes null so it is never read as "the trial
 *  cap is spent". */
export function seatPaymentMethodReason(value: unknown): SeatPaymentMethodReason {
  return value === 'cap-reached' ? value : null;
}

/**
 * What the seat modal knows about the money, after defaulting away the fields
 * an older backend does not send. `amountDueTodayCents` is what Stripe collects
 * now; `recurringAmountCents` is the per-period figure taken from the seat
 * price, because a card-free trial has no invoice preview to carry it.
 */
export interface SeatPreviewView {
  amountDueTodayCents: number;
  recurringAmountCents: number | null;
  currency: string;
  requiresPaymentMethod: boolean;
  paymentMethodReason: SeatPaymentMethodReason;
  isTrial: boolean;
  trialEnd: string | null;
  trialSeatsRemaining: number | null;
}

interface SeatConfirmationStageProps {
  preview: SeatPreviewView;
  email: string;
  paidRole: 'ADMIN' | 'MEMBER';
  /** Pre-formatted trial-end date, or null when the backend sent none. */
  trialEndLabel: string | null;
  onBack: () => void;
  onConfirm: () => void;
  busy: boolean;
}

/** The forward-looking price, or the date alone when there is no figure to
 *  give. Split out so the money rows below stay flat and readable. */
function RecurringDisclosure({
  preview,
  trialEndLabel,
}: {
  preview: SeatPreviewView;
  trialEndLabel: string | null;
}) {
  // Stripe reports no invoice for a card-free trial, and the list price is
  // wrong whenever a discount applies, so the backend sends null in both
  // cases. Name the date and omit the figure: a zero here reads as "this seat
  // is free forever".
  if (preview.recurringAmountCents === null) {
    return (
      <Text fontSize="xs" color="text.muted" mt={2}>
        {`Billed for this seat when your trial ends${trialEndLabel ? ` on ${trialEndLabel}` : ''}.`}
      </Text>
    );
  }
  // Same size and weight as the charged-today row above. The recurring figure
  // is the only disclosure of what the customer actually pays, so it must not
  // be quieter than the $0.00 it follows.
  //
  // The figure is `unit_amount x nextQuantity`: the whole subscription, not
  // this seat. On a screen that is otherwise about one seat ("Billed for this
  // seat" is the sibling branch), "Then, per period" read as the price OF that
  // seat, so a workspace adding its sixth seat saw $594.00 against one
  // invitee. The label has to carry the scope; the number is correct.
  return (
    <>
      <HStack justify="space-between" mt={2}>
        <Text fontSize="sm" color="text.secondary">
          New subscription total, per period
        </Text>
        <Text fontSize="md" fontWeight="semibold">
          {formatUsd(preview.recurringAmountCents, preview.currency)}
        </Text>
      </HStack>
      <Text fontSize="xs" color="text.muted" mt={1}>
        {trialEndLabel
          ? `Starts on ${trialEndLabel}, when your trial ends.`
          : 'Starts when your trial ends.'}
      </Text>
    </>
  );
}

/**
 * Seat-purchase confirmation: what is collected today, and what is collected
 * from here on.
 *
 * Every money claim on this screen is licensed by the money, never by
 * `preview.isTrial`. A card-backed trial can still have a non-zero preview (a
 * pending invoice item, a metered line), and "nothing is charged today" above a
 * real charge is the exact failure this screen exists to avoid. `isTrial` still
 * governs the FORWARD-looking half, which is a fact about the subscription.
 */
export function SeatConfirmationStage({
  preview,
  email,
  paidRole,
  trialEndLabel,
  onBack,
  onConfirm,
  busy,
}: SeatConfirmationStageProps) {
  const nothingDueToday = preview.amountDueTodayCents === 0;
  const trialAndFree = preview.isTrial && nothingDueToday;
  const trialBannerCopy = trialEndLabel
    ? `You are on a free trial, so nothing is charged today. Billing for this seat starts when your trial ends on ${trialEndLabel}.`
    : 'You are on a free trial, so nothing is charged today. Billing for this seat starts when your trial ends.';

  return (
    <>
      <ModalBody pb={2}>
        <VStack spacing={4} align="stretch">
          <StatusBanner
            tone="info"
            title="You’re adding a paid seat to your Team workspace"
            description={
              trialAndFree
                ? trialBannerCopy
                : 'The Stripe charge below is pro-rated for the time remaining in the current billing period. Your subscription rate will increase from the next renewal.'
            }
          />

          <Box>
            <HStack justify="space-between">
              <Text fontSize="sm" color="text.secondary">
                Inviting
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {email}
              </Text>
            </HStack>
            <HStack justify="space-between" mt={2}>
              <Text fontSize="sm" color="text.secondary">
                Role
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {paidRole === 'ADMIN' ? 'Admin' : 'Member'}
              </Text>
            </HStack>
            <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px">
              <Text fontSize="sm" color="text.secondary">
                {trialAndFree ? 'Charged today' : 'Pro-rated charge today'}
              </Text>
              <Text fontSize="md" fontWeight="semibold">
                {formatUsd(preview.amountDueTodayCents, preview.currency)}
              </Text>
            </HStack>
            {preview.isTrial && (
              <RecurringDisclosure preview={preview} trialEndLabel={trialEndLabel} />
            )}
            {preview.isTrial && preview.trialSeatsRemaining !== null && (
              <Text fontSize="xs" color="text.muted" mt={1}>
                {preview.trialSeatsRemaining} of {TRIAL_CARDLESS_SEAT_CAP} card-free trial seats
                remaining.
              </Text>
            )}
          </Box>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onBack} mr={2}>
          Back
        </Button>
        <Button isLoading={busy} onClick={onConfirm}>
          Confirm and invite
        </Button>
      </ModalFooter>
    </>
  );
}
